/* eslint-disable import/no-cycle */
import { PinIcon } from '@eleveo/icons-react';
import { differenceInMinutes, eachDayOfInterval } from 'date-fns';
import { DataLabelsFormatterContextObject, Point } from 'highcharts';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { ScheduledAgentInput, ScheduleDaySettingsInput } from '../../../graphql/components';
import { DateType } from '../../../types';
import {
  DEFAULT_OUTLINE_COLOR_OPACITY,
  formatScheduleElementRGBAColor,
  getContrastColor,
  getTextShadowOfScheduleElement,
  transformColorRGBAToObj,
} from '../../../utils/chart';
import { MIN_15_DROP_ELEMENT_WIDTH, defaultAgentDaySettings } from '../config';
import { ChartScheduleElement, SchedulingState } from '../types';

export function formatScheduleElementLabel(this: DataLabelsFormatterContextObject): string {
  const element = this.point as Point & ChartScheduleElement;

  if (!element) {
    return '';
  }

  const elementName = element.scheduleElementName.toUpperCase();
  const isPinned = element.pinned;

  /**
   * when rendering production (shadow) elements, color is of type object containing pattern of stripes,
   * therefore we need to use "defaultColor" prop as workaround
   */
  // @ts-expect-error: incompatible type 😥
  const color = typeof element.color === 'string' ? element.color : element.defaultColor;

  const xAxisMin = this.series.chart.xAxis[0].min as number;
  const xAxisMax = this.series.chart.xAxis[0].max as number;

  const contrastColor = getContrastColor(transformColorRGBAToObj(color));
  const outlineColor = formatScheduleElementRGBAColor(getContrastColor(contrastColor), DEFAULT_OUTLINE_COLOR_OPACITY);

  /**
   * element could overflow to surrounding days, so we need to consider
   * only the relevant part of period in viewed day
   */
  const start = Math.max(xAxisMin, element.start);
  const end = Math.min(xAxisMax, element.end);

  let textContent = '';
  let iconContent = '';

  const elementDurationInMinutes = differenceInMinutes(end, start);
  const elementSizeInPx = Math.ceil((elementDurationInMinutes / 15) * MIN_15_DROP_ELEMENT_WIDTH);

  if (elementDurationInMinutes <= 5) {
    if (isPinned) {
      // show smaller size of pinned icon to fit into available space
      iconContent = renderToStaticMarkup(
        <PinIcon className="pinned-element-icon text-2xs" style={{ stroke: outlineColor }} />
      );
    }

    // no text content - it wouldn't fit into element size anyway
  } else if (elementDurationInMinutes <= 10) {
    if (isPinned) {
      // pin icon without margin, no text content!
      iconContent = renderToStaticMarkup(
        <PinIcon className="pinned-element-icon text-xs" style={{ stroke: outlineColor }} />
      );
    } else {
      // show first letter of schedule name
      textContent = elementName.slice(0, 1);
    }
  } else {
    const pinnedIcon = isPinned
      ? renderToStaticMarkup(<PinIcon className="pinned-element-icon ml-1" style={{ stroke: outlineColor }} />)
      : '';

    const sizeOfPinIcon = isPinned ? 12 : 0;
    if (elementSizeInPx < element.scheduleElementNameSize + sizeOfPinIcon) {
      if (isPinned) {
        // pin icon with margin
        iconContent = pinnedIcon;
        // show first (two) letter(s) of schedule based on elements duration
        textContent = elementName.slice(0, elementDurationInMinutes > 15 ? 2 : 1);
      } else {
        // show first two letters of schedule name
        textContent = elementName.slice(0, 2);
      }
    } else {
      if (isPinned) {
        iconContent = pinnedIcon;
      }

      // show full schedule name
      textContent = elementName;
    }
  }

  if (textContent === '') {
    // skip rendering of unnecessary span element with styling for empty text
    return iconContent;
  }

  return `<span style="text-shadow: ${getTextShadowOfScheduleElement(
    contrastColor
  )}; color: ${formatScheduleElementRGBAColor(contrastColor)};">${textContent}</span>${iconContent}`;
}

export const getScheduledAgents = (agentsIds: string[]): ScheduledAgentInput[] =>
  agentsIds.map((agentId, i) => ({
    agentId,
    displayOrder: i + 1,
  }));

export const getDaysSettings = (daysSettings: SchedulingState['daysSettings']): ScheduleDaySettingsInput[] =>
  Object.values(daysSettings).map(({ dayIndex, agents }) => ({
    dayIndex,
    agents: Object.values(agents),
  }));

export const getDefaultDaySettings = <T extends { agentId: string }>(
  startDate: DateType,
  endDate: DateType,
  agents: T[] = []
): ScheduleDaySettingsInput[] =>
  eachDayOfInterval({ start: new Date(startDate), end: new Date(endDate) }).map((_, i) => ({
    dayIndex: i,
    agents: agents.map(({ agentId }) => ({
      ...defaultAgentDaySettings,
      agentId,
    })),
  }));
